import { useGetList, type Identifier } from "ra-core";
import { TASK_STATUS_FILTERS } from "@/components/atomic-crm/tasks/taskConstants";
import type { Contact, ContactNote, Deal } from "@/components/atomic-crm/types";
import type {
  ClientInvoice,
  Contract,
  Proposal,
  Ticket,
} from "@/modules/types";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext";
import { buildOpenDealsFilter } from "@/modules/deals/openDealFilters";

const countQuery = (_resource: string, filter: Record<string, unknown>) => ({
  filter,
  pagination: { page: 1, perPage: 1 },
  sort: { field: "id", order: "DESC" as const },
});

/** Counts for one person: only records linked to this contact. */
export const useContactTabCounts = (contact: Contact | null | undefined) => {
  const { dealPipelineStatuses, dealStages } = useConfigurationContext();
  const contactId = contact?.id ?? "";
  const companyId = contact?.company_id ?? "";
  const enabled = contactId !== "" && contactId != null;
  const companyEnabled = companyId !== "" && companyId != null;
  const staleTime = 30_000;

  const { total: projects = 0 } = useGetList<Deal>(
    "deals",
    countQuery("deals", { "contact_ids@cs": `{${contactId}}` }),
    { staleTime, enabled },
  );

  const { total: deals = 0 } = useGetList<Deal>(
    "deals",
    countQuery(
      "deals",
      buildOpenDealsFilter(dealPipelineStatuses, dealStages, {
        "contact_ids@cs": `{${contactId}}`,
      }),
    ),
    { staleTime, enabled },
  );

  const { total: proposals = 0 } = useGetList<Proposal>(
    "proposals",
    countQuery("proposals", { "contact_id@eq": contactId }),
    { staleTime, enabled },
  );

  const { total: contracts = 0 } = useGetList<Contract>(
    "contracts",
    countQuery("contracts", { "contact_id@eq": contactId }),
    { staleTime, enabled },
  );

  const { total: invoices = 0 } = useGetList<ClientInvoice>(
    "client_invoices",
    countQuery("client_invoices", { "contact_id@eq": contactId }),
    { staleTime, enabled },
  );

  const { data: contactDeals = [] } = useGetList<Deal>(
    "deals",
    {
      filter: { "contact_ids@cs": `{${contactId}}` },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "id", order: "ASC" },
    },
    { staleTime, enabled },
  );

  const dealIds = contactDeals.map((deal) => deal.id);
  const paymentsFilter =
    dealIds.length > 0
      ? { "deal_id@in": `(${dealIds.join(",")})` }
      : { "deal_id@eq": -1 };

  const { total: invoicePayments = 0 } = useGetList<ClientInvoice>(
    "client_invoices",
    countQuery("client_invoices", {
      "contact_id@eq": contactId,
      "@or": {
        "amount_paid@gt": 0,
        "status@eq": "paid",
      },
    }),
    { staleTime, enabled },
  );

  const { total: projectPayments = 0 } = useGetList(
    "deal_client_payments",
    countQuery("deal_client_payments", paymentsFilter),
    { staleTime, enabled: enabled && dealIds.length > 0 },
  );

  const contactFilter = { "contact_id@eq": contactId };

  const { total: tasks = 0 } = useGetList(
    "tasks",
    countQuery("tasks", { ...contactFilter, ...TASK_STATUS_FILTERS.open }),
    { staleTime, enabled },
  );

  const { total: notes = 0 } = useGetList(
    "contact_notes",
    countQuery("contact_notes", { ...contactFilter, "parent_id@is": null }),
    { staleTime, enabled },
  );

  const { total: referrals = 0 } = useGetList<Contact>(
    "contacts",
    countQuery("contacts", { "referred_by_contact_id@eq": contactId }),
    { staleTime, enabled },
  );

  const { total: tickets = 0 } = useGetList<Ticket>(
    "tickets",
    countQuery("tickets", { "contact_id@eq": contactId }),
    { staleTime, enabled },
  );

  const todayKey = new Date().toISOString().slice(0, 10);
  const { total: overdueTasks = 0 } = useGetList(
    "tasks",
    countQuery("tasks", {
      ...contactFilter,
      ...TASK_STATUS_FILTERS.open,
      "due_date@lt": todayKey,
    }),
    { staleTime, enabled },
  );

  const { data: latestNotes = [] } = useGetList<ContactNote>(
    "contact_notes",
    {
      filter: contactFilter,
      pagination: { page: 1, perPage: 1 },
      sort: { field: "date", order: "DESC" as const },
    },
    { staleTime, enabled },
  );

  return {
    deals,
    projects,
    proposals,
    contracts,
    invoices,
    payments: projectPayments + invoicePayments,
    tasks,
    notes,
    referrals,
    tickets,
    overdueTasks,
    lastActivityAt: latestNotes[0]?.date ?? null,
    contactDeals,
    contactIds: enabled ? ([contactId] as Identifier[]) : ([] as Identifier[]),
    companyId,
    hasCompany: companyEnabled,
  };
};
